import React, { useState, useRef, useEffect } from "react";

/**
 * TRAE.AI STYLE GLITCH MOTION EFFECT WITH SCROLL SHUTTER REVEAL
 * 
 * PERFORMANCE OPTIMIZATIONS:
 * - Uses requestAnimationFrame with ticking flag to prevent forced reflow
 * - Batches DOM reads before state updates
 * - Uses transform and opacity for animations (GPU composited)
 * - Added will-change hints for animated elements
 *
 * LIBRARIES USED:
 * - React (hooks: useState, useRef, useEffect, useCallback)
 * - Tailwind CSS (for utility classes)
 *
 * HOW SCROLL SHUTTER WORKS:
 * - Scroll position is tracked when component enters viewport
 * - Text is divided into vertical strips (shutters)
 * - Each strip reveals progressively based on scroll progress (0-100%)
 * - Fully revealed when scroll progress reaches 100%
 *
 * HOW CURSOR POSITION MAPS TO DISTORTION:
 * - Mouse position is tracked relative to the text container
 * - X and Y positions are normalized to -1 to 1 range
 * - These values drive the transform translations of glitch layers
 * - Glitch effect only activates when fully revealed (scroll progress > 95%)
 *
 * CUSTOMIZATION:
 * - GLITCH_INTENSITY: Multiply distortion values (currently 1.5)
 * - SLICE_COUNT: Number of horizontal glitch slices (currently 8)
 * - SHUTTER_COUNT: Number of vertical reveal strips (currently 20)
 * - Reveal speed: Adjust scroll calculation in useEffect
 */

const GlitchText = () => {
  // State for mouse position (normalized -1 to 1)
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0); // 0-1 range for scroll reveal
  const containerRef = useRef(null);

  // RAF throttling refs to prevent forced reflow
  const scrollTickingRef = useRef(false);
  const mouseTickingRef = useRef(false);

  // INTENSITY CONTROLS - Adjust these to change effect strength
  const GLITCH_INTENSITY = 1.5; // Multiplier for distortion (1 = normal, 2 = double)
  const SLICE_COUNT = 8; // Number of horizontal slices for glitch
  const SHUTTER_COUNT = 20; // Number of vertical strips for reveal

  // Track scroll progress for shutter reveal effect (RAF throttled)
  useEffect(() => {
    const handleScroll = () => {
      // Skip if already processing a frame
      if (scrollTickingRef.current) return;

      scrollTickingRef.current = true;

      requestAnimationFrame(() => {
        if (!containerRef.current) {
          scrollTickingRef.current = false;
          return;
        }

        // === DOM READS (batched together) ===
        const rect = containerRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;

        // === CALCULATIONS (no DOM access) ===
        const elementTop = rect.top;
        const elementBottom = rect.bottom;
        const elementHeight = rect.height;

        let progress = 0;

        // Element is entering from bottom of viewport
        if (elementTop < windowHeight && elementBottom > 0) {
          const visibleBottom = Math.min(
            elementHeight,
            windowHeight - elementTop,
          );
          const visibleHeight = Math.max(0, visibleBottom);
          progress = Math.min(visibleHeight / elementHeight, 1);
          progress = Math.pow(progress, 0.8); // Easing
        } else if (elementTop <= 0 && elementBottom > windowHeight) {
          progress = 1; // Element fills viewport
        } else if (elementBottom <= windowHeight && elementTop < 0) {
          progress = 1; // Element is above viewport
        }

        // === STATE UPDATE (batched by React) ===
        setScrollProgress(progress);
        scrollTickingRef.current = false;
      });
    };

    handleScroll(); // Initial check
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  // Track mouse movement for glitch effect (RAF throttled, only when fully revealed)
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!containerRef.current || !isHovering) return;

      // Skip if already processing a frame
      if (mouseTickingRef.current) return;

      mouseTickingRef.current = true;

      requestAnimationFrame(() => {
        if (!containerRef.current) {
          mouseTickingRef.current = false;
          return;
        }

        // === DOM READ ===
        const rect = containerRef.current.getBoundingClientRect();

        // === CALCULATIONS (no DOM access) ===
        const x = (e.clientX - rect.left - rect.width / 2) / (rect.width / 2);
        const y = (e.clientY - rect.top - rect.height / 2) / (rect.height / 2);

        // === STATE UPDATE ===
        setMousePos({
          x: Math.max(-1, Math.min(1, x)),
          y: Math.max(-1, Math.min(1, y)),
        });
        mouseTickingRef.current = false;
      });
    };

    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [isHovering]);

  // Generate random offset for organic glitch feel
  const getRandomOffset = (seed) => {
    return (Math.sin(seed * 12.9898) * 43758.5453) % 1;
  };

  // Calculate transform values based on mouse position
  const getLayerTransform = (layerIndex, intensity) => {
    if (!isHovering || scrollProgress < 0.95) return 'translate(0, 0)';

    // Apply intensity multiplier
    const xOffset = mousePos.x * intensity * GLITCH_INTENSITY;
    const yOffset = mousePos.y * intensity * GLITCH_INTENSITY;

    return `translate(${xOffset}px, ${yOffset}px)`;
  };

  // Generate clip-path for horizontal slices (glitch effect)
  const getSliceClipPath = (index, total) => {
    const sliceHeight = 100 / total;
    const top = sliceHeight * index;
    const bottom = sliceHeight * (index + 1);
    return `polygon(0 ${top}%, 100% ${top}%, 100% ${bottom}%, 0 ${bottom}%)`;
  };

  // Generate clip-path for vertical shutters (scroll reveal)
  const getShutterClipPath = (index, total, progress) => {
    const sliceWidth = 100 / total;
    const left = sliceWidth * index;
    const right = sliceWidth * (index + 1);

    // Calculate reveal progress for this specific shutter
    // Each shutter reveals in sequence based on scroll progress
    const shutterStart = index / total;
    const shutterEnd = (index + 1) / total;
    const shutterProgress = Math.max(0, Math.min(1, (progress - shutterStart) / (shutterEnd - shutterStart)));

    // Reveal from top to bottom
    const revealHeight = shutterProgress * 100;

    return `polygon(${left}% 0%, ${right}% 0%, ${right}% ${revealHeight}%, ${left}% ${revealHeight}%)`;
  };

  return (
    <div className="bg-[#FF6B1A] items-center justify-center overflow-hidden">
      <div
        ref={containerRef}
        className="relative w-full cursor-default"
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => {
          setIsHovering(false);
          setMousePos({ x: 0, y: 0 });
        }}
      >
        {/* Main text container with stacking context */}
        <div className="relative">
          {/* SHUTTER REVEAL LAYERS - Vertical strips that reveal on scroll */}
          {[...Array(SHUTTER_COUNT)].map((_, i) => (
            <div
              key={`shutter-${i}`}
              className="absolute top-0 left-0 w-full h-full pointer-events-none z-40"
              style={{
                clipPath: getShutterClipPath(i, SHUTTER_COUNT, scrollProgress),
                transition: "clip-path 0.1s linear",
                willChange: "clip-path", // GPU layer hint for smooth animation
              }}
            >
              <p
                className=" text-[clamp(60px,20vw,300px)] py-16 font-black text-center font-unbounded leading-none tracking-tight text-white select-none"
                style={{
                  letterSpacing: "0.01em",
                }}
              >
                QBits9
              </p>
            </div>
          ))}

          {/* BASE LAYER - Hidden until fully revealed, then shows for glitch effect */}
          <p
            className="relative z-30  text-[clamp(60px,20vw,300px)] py-16 font-black text-center font-unbounded leading-none tracking-tight select-none"
            style={{
              letterSpacing: "0.01em",
              opacity: scrollProgress >= 0.95 ? 1 : 0,
              color: "white",
              transition: "opacity 0.3s ease-out",
              willChange: "opacity", // GPU layer hint for smooth fade
            }}
          >
            QBits9
          </p>

          {/* GLITCH LAYERS - Only visible on hover when fully revealed */}
          {isHovering && scrollProgress >= 0.95 && (
            <>
              {/* RED CHANNEL SHIFT - Full text layer */}
              <div
                className="absolute top-0 left-0 w-full h-full pointer-events-none z-20"
                style={{
                  transform: getLayerTransform(1, 8),
                  transition: "transform 50ms ease-out",
                  opacity: 0.7,
                  mixBlendMode: "screen",
                  willChange: "transform", // GPU layer hint for smooth glitch
                }}
              >
                <p
                  className=" text-[clamp(60px,20vw,300px)] py-16 font-black text-center leading-none tracking-tight text-red-500 select-none font-unbounded"
                  style={{
                    letterSpacing: "0.01em",
                  }}
                >
                  QBits9
                </p>
              </div>

              {/* CYAN CHANNEL SHIFT - Full text layer */}
              <div
                className="absolute top-0 left-0 w-full h-full pointer-events-none z-20"
                style={{
                  transform: getLayerTransform(2, -6),
                  transition: "transform 50ms ease-out",
                  opacity: 0.7,
                  mixBlendMode: "screen",
                  willChange: "transform", // GPU layer hint for smooth glitch
                }}
              >
                <p
                  className=" text-[clamp(60px,20vw,300px)] py-16  font-black text-center leading-none tracking-tight text-cyan-400 select-none font-unbounded"
                  style={{
                    letterSpacing: "0.01em",
                  }}
                >
                  QBits9
                </p>
              </div>

              {/* HORIZONTAL SLICES - Creates fragmented glitch effect */}
              {[...Array(SLICE_COUNT)].map((_, i) => {
                const randomness = getRandomOffset(i);
                const intensity = 15 + randomness * 15; // 15-30px range
                const delay = i * 5; // Stagger effect slightly

                return (
                  <div
                    key={`slice-${i}`}
                    className="absolute top-0 left-0 w-full h-full pointer-events-none z-10"
                    style={{
                      clipPath: getSliceClipPath(i, SLICE_COUNT),
                      transform: getLayerTransform(
                        i + 3,
                        intensity * (i % 2 === 0 ? 1 : -1),
                      ),
                      transition: `transform ${50 + delay}ms ease-out`,
                      opacity: 0.3,
                      willChange: "transform", // GPU layer hint for smooth slice animation
                    }}
                  >
                    <p
                      className=" text-[clamp(60px,20vw,300px)] py-16 font-black text-center leading-none font-unbounded tracking-tight select-none"
                      style={{
                        letterSpacing: "0.01em",
                        color:
                          i % 3 === 0
                            ? "#00ffff"
                            : i % 3 === 1
                              ? "#ff00ff"
                              : "#ffff00",
                      }}
                    >
                      QBits9
                    </p>
                  </div>
                );
              })}

              {/* SCANLINE EFFECT - Adds digital distortion feel */}
              <div
                className="absolute top-0 left-0 w-full h-full pointer-events-none z-25"
                style={{
                  background:
                    "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.03) 2px, rgba(255,255,255,0.03) 4px)",
                  opacity: isHovering ? 1 : 0,
                  transition: "opacity 200ms ease-out",
                }}
              />

              {/* NOISE OVERLAY - Subtle texture */}
              <div
                className="absolute top-0 left-0 w-full h-full pointer-events-none z-25"
                style={{
                  background:
                    "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.05'/%3E%3C/svg%3E\")",
                  opacity: isHovering ? 1 : 0,
                  transition: "opacity 200ms ease-out",
                }}
              />
            </>
          )}
        </div>

        {/* Scroll Progress Indicator (for debugging - remove in production) */}
        {/* <div className="text-center mt-4 text-white/50 text-sm">
          Scroll Progress: {Math.round(scrollProgress * 100)}%
        </div> */}
      </div>
    </div>
  );
};

export default GlitchText;
